/**
 * Stundenwächter: bringt den Nutzer zum Thema der gerade laufenden Stunde.
 *
 * Zwei Wege, je nachdem was der Nutzer gerade tut:
 *   - App war zu / lag im Hintergrund  -> direkt ins Thema springen
 *   - App wird gerade aktiv genutzt    -> Popup schlägt den Wechsel vor
 *
 * Damit derselbe Slot nicht wiederholt aufpoppt, merkt sich der Wächter pro
 * Tag den zuletzt behandelten Slot in localStorage. Das überlebt auch einen
 * Neustart der Capacitor-App.
 */

import { h } from "@/lib/dom";
import { button, toast } from "@/components/ui";
import { subjectIcon } from "@/lib/subjectIcons";
import { defaultColor } from "@/lib/colors";
import { navigate } from "@/app/router";
import { auth, timetableService, subjectsService } from "@/services";
import { topicsService } from "@/services/topics.service";
import { currentSlot, slotKey } from "./schedule";
import type { Subject } from "@/types/models";

const SEEN_KEY = "lernapp.timetable.lastSlot";
/** Ab wie vielen Minuten Inaktivität ein Seitenaufruf als „Kaltstart" gilt. */
const COLD_START_MS = 90_000;

/** Ein Editor-Tab o. Ä. soll nicht mitten im Schreiben weggerissen werden. */
const PROTECTED_ROUTES = [/^\/topic\/[^/]+\/edit\//, /^\/topic\/[^/]+\/learn/];

interface SeenState {
  date: string;
  slot: string;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function readSeen(): SeenState | null {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw ? (JSON.parse(raw) as SeenState) : null;
  } catch {
    return null;
  }
}

/** Merkt sich, dass dieser Slot behandelt wurde – auch nach manuellem Öffnen. */
export function markSlotSeen(key: string): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify({ date: today(), slot: key } satisfies SeenState));
  } catch {
    /* ohne localStorage schlägt der Vorschlag ggf. erneut vor – unkritisch */
  }
}

function alreadySeen(key: string): boolean {
  const seen = readSeen();
  return !!seen && seen.date === today() && seen.slot === key;
}

function onProtectedRoute(): boolean {
  const path = location.hash.replace(/^#/, "").split("?")[0];
  return PROTECTED_ROUTES.some((re) => re.test(path));
}

/** Popup-Vorschlag, wenn die App gerade aktiv benutzt wird. */
function suggest(subject: Subject, topic: { id: string; title: string }, key: string): void {
  const accent = subject.color || defaultColor();
  const card = h(
    "div",
    { class: "lesson-prompt", style: `--accent:${accent}` },
    h("span", { class: "lp-icon", html: subjectIcon(subject.icon, 20) }),
    h(
      "div",
      { class: "lp-text" },
      h("div", { class: "lp-title" }, `${subject.title} hat begonnen`),
      h("div", { class: "lp-sub" }, topic.title)
    ),
    h(
      "div",
      { class: "lp-actions" },
      button({
        label: "Öffnen",
        variant: "primary",
        size: "sm",
        onClick: () => {
          markSlotSeen(key);
          card.remove();
          navigate(`/topic/${topic.id}`);
        },
      }),
      button({
        icon: "x",
        variant: "ghost",
        size: "sm",
        title: "Später",
        onClick: () => {
          markSlotSeen(key);
          card.remove();
        },
      })
    )
  );
  document.body.appendChild(card);
  // Nicht ewig stehen lassen; „gesehen" wird der Slot erst durch eine Aktion,
  // damit ein übersehener Vorschlag beim nächsten Öffnen erneut greift.
  window.setTimeout(() => card.remove(), 30_000);
}

/**
 * Prüft, ob gerade eine Unterrichtsstunde läuft, und reagiert entsprechend.
 * `cold` = App wurde neu geöffnet / kam aus dem Hintergrund zurück.
 */
async function check(cold: boolean): Promise<void> {
  if (!auth.isAuthenticated()) return;
  if (onProtectedRoute()) return;

  try {
    const [settings, slots] = await Promise.all([
      timetableService.getSettings(),
      timetableService.listSlots(),
    ]);
    const hit = currentSlot(slots, settings.periods);
    if (!hit) return;

    const key = slotKey(hit.slot.weekday, hit.period);
    if (alreadySeen(key)) return;

    const subject = (await subjectsService.list()).find((s) => s.id === hit.slot.subject);
    if (!subject) return;

    const topic = await topicsService.currentBySubject(subject.id);
    if (!topic) {
      if (cold) toast(`${subject.title} läuft gerade – noch kein Thema angelegt.`, "info");
      return;
    }

    // Springt NIE mehr von allein – ein `visibilitychange` kann auf manchen
    // Geräten/Browsern (v. a. iOS Safari) mehrfach kurz hintereinander
    // feuern, ohne dass der Nutzer wirklich weg war; jedes Mal ungefragt
    // wegzunavigieren sah dann aus wie ein wiederholtes Neuladen der Seite.
    // Der Vorschlag als Karte (mit Öffnen/Später) bleibt in jedem Fall
    // wegklickbar und übernimmt jetzt auch den früheren "cold"-Fall.
    suggest(subject, topic, key);
  } catch {
    // Der Stundenplan ist ein Komfort-Feature: still scheitern statt stören.
  }
}

let started = false;
let lastHidden = 0;
let pollTimer: number | undefined;

/**
 * Startet den Wächter. Einmalig aus main.ts aufrufen.
 * Ausgelöst wird durch: App-Start, Rückkehr aus dem Hintergrund und einen
 * Minutentakt, der den Stundenwechsel bei geöffneter App bemerkt.
 */
export function startLessonWatch(): void {
  if (started) return;
  started = true;

  void check(true);

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      lastHidden = Date.now();
      return;
    }
    // Kurzes Wegklicken ist kein Kaltstart – sonst springt die App bei jedem
    // Tab-Wechsel weg.
    const away = Date.now() - lastHidden;
    void check(away > COLD_START_MS);
  });

  window.clearInterval(pollTimer);
  pollTimer = window.setInterval(() => {
    if (!document.hidden) void check(false);
  }, 60_000);
}
